import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import PublicSongReader from '@/components/chordpro/PublicSongReader';
import { CalendarDays, Clock3, ExternalLink, MapPin, Music2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ITEM_TYPES = {
  lied: 'Lied', gebed: 'Gebed', mededeling: 'Mededeling', collecte: 'Collecte',
  preek: 'Preek', avondmaal: 'Avondmaal', vrij_tekstblok: 'Vrij tekstblok', pauze_overgang: 'Pauze / overgang',
};
const SONG_PARAM = 'song';

const formatDate = (value) => value
  ? new Intl.DateTimeFormat('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(`${value}T12:00:00`))
  : '';

const PublicSetlistPage = () => {
  const { serviceId } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    axios.get(`${API}/public/worship/services/${serviceId}`)
      .then((response) => setService(response.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [serviceId]);

  const songMap = useMemo(() => service?.songs || {}, [service]);

  // Setlist items usable as "songs" for the reader/navigation: real song
  // items (item_type === 'lied') that have either loaded song content or a
  // PDF-only link. Other item types (prayer, sermon, ...) never enter this
  // list, so Previous/Next only ever steps between actual songs.
  const songItems = useMemo(() => (service?.setlist || []).filter(
    (item) => item.item_type === 'lied' && ((item.song_id && songMap[item.song_id]) || item.pdf_link_url)
  ), [service, songMap]);

  const activeSongId = searchParams.get(SONG_PARAM);
  const activeIndex = activeSongId ? songItems.findIndex((item) => item.id === activeSongId) : -1;
  const activeItem = activeIndex >= 0 ? songItems[activeIndex] : null;

  const openItem = (item) => {
    if (item.item_type !== 'lied') return;
    const song = item.song_id ? songMap[item.song_id] : null;
    if (!song && !item.pdf_link_url) {
      toast.error('Voor dit onderdeel is geen lied of PDF gekoppeld');
      return;
    }
    setSearchParams({ [SONG_PARAM]: item.id });
  };

  const closeReader = () => {
    setSearchParams({}, { replace: true });
  };

  const goToIndex = (index) => {
    const target = songItems[index];
    if (target) setSearchParams({ [SONG_PARAM]: target.id }, { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30 flex items-center justify-center">
        <div className="animate-pulse text-slate-500">Setlist laden...</div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30 flex items-center justify-center p-4">
        <Card className="border-0 shadow-sm max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Music2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <h1 className="text-lg font-semibold text-slate-900 mb-1">Setlist niet gevonden</h1>
            <p className="text-sm text-slate-500">Deze link is ongeldig of de dienst bestaat niet (meer).</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (activeItem) {
    const song = activeItem.song_id ? songMap[activeItem.song_id] : null;
    return (
      <PublicSongReader
        item={activeItem}
        song={song}
        index={activeIndex}
        total={songItems.length}
        onBack={closeReader}
        onPrev={() => goToIndex(activeIndex - 1)}
        onNext={() => goToIndex(activeIndex + 1)}
        hasPrev={activeIndex > 0}
        hasNext={activeIndex < songItems.length - 1}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30" data-testid="public-setlist-page">
      <Toaster position="top-right" richColors />
      <div className="mx-auto max-w-2xl px-4 py-8">
        <p className="text-sm font-medium text-teal-700">{service.organization_name}</p>
        <h1 className="mt-1 text-2xl font-bold text-slate-900">{service.title}</h1>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
          <span className="flex items-center gap-1"><CalendarDays className="w-4 h-4" />{formatDate(service.service_date)}</span>
          <span className="flex items-center gap-1"><Clock3 className="w-4 h-4" />{service.start_time}</span>
          {service.location && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{service.location}</span>}
        </div>

        <div className="mt-6 space-y-2">
          {service.setlist.map((item, position) => {
            const clickable = item.item_type === 'lied' && ((item.song_id && songMap[item.song_id]) || item.pdf_link_url);
            return (
              <button
                key={item.id}
                type="button"
                disabled={!clickable}
                onClick={() => openItem(item)}
                data-testid="setlist-item"
                className={`w-full rounded-xl border border-slate-200 bg-white p-4 text-left transition ${clickable ? 'hover:border-teal-300 hover:shadow-md cursor-pointer' : 'cursor-default opacity-80'}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-500" data-testid="setlist-item-position">
                    {position + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900 truncate">{item.title}</p>
                    <p className="text-xs text-slate-500">
                      {ITEM_TYPES[item.item_type] || item.item_type}
                      {item.key ? ` · ${item.key}` : ''}
                      {item.bpm ? ` · ${item.bpm} BPM` : ''}
                      {item.duration_minutes ? ` · ${item.duration_minutes} min` : ''}
                    </p>
                  </div>
                  {clickable && <ExternalLink className="w-4 h-4 shrink-0 text-teal-600" />}
                </div>
              </button>
            );
          })}
          {!service.setlist.length && (
            <Card className="border-0 shadow-sm"><CardContent className="py-8 text-center text-sm text-slate-500">Nog geen setlist beschikbaar.</CardContent></Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicSetlistPage;
